import {KycProps, KycStatus, KycSocialLinks, KycDocumentUrls} from "./kyc.types";

/**
 * KycRecord entity.
 * Encapsulates KYC data and business rules.
 *
 * Pure TypeScript - no React imports.
 */
export class KycRecord {
  private readonly props: KycProps;

  private constructor(props: KycProps) {
    this.props = props;
  }

  /**
   * Creates a KycRecord entity with validation.
   * @throws Error if required fields are missing
   */
  static create(props: KycProps): KycRecord {
    if (props.id === undefined || props.id === null) {
      throw new Error("KYC record id is required");
    }

    if (!props.userId) {
      throw new Error("KYC record userId is required");
    }

    if (!props.fullName) {
      throw new Error("KYC record fullName is required");
    }

    return new KycRecord(props);
  }

  // ==================== Getters ====================

  get id(): number {
    return this.props.id;
  }

  get userId(): number {
    return this.props.userId;
  }

  get fullName(): string {
    return this.props.fullName;
  }

  get businessName(): string {
    return this.props.businessName;
  }

  get serviceDescription(): string {
    return this.props.serviceDescription;
  }

  get email(): string {
    return this.props.email;
  }

  get socialLinks(): KycSocialLinks {
    return this.props.socialLinks;
  }

  get country(): string {
    return this.props.country;
  }

  get address(): string {
    return this.props.address;
  }

  get postalCode(): string {
    return this.props.postalCode;
  }

  get phoneNumber(): string | null {
    return this.props.phoneNumber;
  }

  get gender(): string {
    return this.props.gender;
  }

  get dateOfBirth(): string {
    return this.props.dateOfBirth;
  }

  get documents(): KycDocumentUrls {
    return this.props.documents;
  }

  get status(): KycStatus {
    return this.props.status;
  }

  get reasonForRejection(): string | null {
    return this.props.reasonForRejection;
  }

  get createdAt(): string {
    return this.props.createdAt;
  }

  get updatedAt(): string {
    return this.props.updatedAt;
  }

  // ==================== Business Methods ====================

  isInProgress(): boolean {
    return this.props.status === "InProgress";
  }

  isSubmitted(): boolean {
    return this.props.status === "Submitted";
  }

  isActive(): boolean {
    return this.props.status === "Active";
  }

  isFailed(): boolean {
    return this.props.status === "Failed";
  }

  /**
   * Whether this record can be edited (re-submitted).
   * Only InProgress or Failed records can be edited.
   */
  canEdit(): boolean {
    return this.isInProgress() || this.isFailed();
  }

  /**
   * Whether all document URLs are present.
   */
  hasDocuments(): boolean {
    const docs = this.props.documents;
    return !!(docs.proofOfAddressUrl && docs.governmentIdUrl && docs.selfieWithIdUrl);
  }

  /**
   * Returns the raw properties for serialization or mapping.
   */
  toProps(): KycProps {
    return {...this.props};
  }
}
